// DashboardInstructor.tsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { coursesApi, Course } from "../../lib/coursesApi";
import { reviewsApi, Review } from "../../lib/reviewsApi";
import { enrollmentApi } from "../../lib/enrollmentApi";
import {
  BookOpen,
  Users,
  DollarSign,
  Star,
  Eye,
  Edit,
  Plus,
  CheckCircle,
  TrendingUp,
  Loader,
  ChevronRight,
  Calendar,
  Award,
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

const DashboardInstructor = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [recentReviews, setRecentReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollmentTrends, setEnrollmentTrends] = useState<any[]>([]);
  const [coursePerformance, setCoursePerformance] = useState<any[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?._id) return;

      setLoading(true);
      try {
        // Fetch courses
        const coursesRes = await coursesApi.getInstructorCourses(user._id, {
          limit: 50,
        });

        if (coursesRes.success) {
          const coursesData = coursesRes.data || [];
          setCourses(coursesData);

          // Calculate total students and revenue from courses
          let studentCount = 0;
          let revenueSum = 0;

          coursesData.forEach((course: any) => {
            const enrolledCount = course.enrolledStudents?.length || 0;
            studentCount += enrolledCount;
            revenueSum += (course.price || 0) * enrolledCount;
          });

          setTotalStudents(studentCount);
          setTotalRevenue(revenueSum);

          // Prepare course performance data for top courses
          const performanceData = coursesData
            .filter((c) => c.enrolledStudents?.length > 0)
            .slice(0, 5)
            .map((course: any) => ({
              name: course.title?.substring(0, 15) || "Course",
              students: course.enrolledStudents?.length || 0,
              revenue:
                (course.price || 0) * (course.enrolledStudents?.length || 0),
              rating: course.rating || 0,
            }));
          setCoursePerformance(performanceData);
        }

        // Fetch instructor analytics for enrollment trends
        try {
          const analyticsRes = await enrollmentApi.getInstructorAnalytics();
          if (analyticsRes.success && analyticsRes.data) {
            const analytics = analyticsRes.data;

            // Process enrollment trends
            if (
              analytics.enrollmentsOverTime &&
              analytics.enrollmentsOverTime.length > 0
            ) {
              const trends = analytics.enrollmentsOverTime
                .slice(-7)
                .map((item: any) => ({
                  date: new Date(item._id).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  }),
                  enrollments: item.count || 0,
                }));
              setEnrollmentTrends(trends);
            } else {
              // Generate sample data for demo
              const sampleTrends = [
                { date: "Mon", enrollments: 5 },
                { date: "Tue", enrollments: 8 },
                { date: "Wed", enrollments: 12 },
                { date: "Thu", enrollments: 7 },
                { date: "Fri", enrollments: 15 },
                { date: "Sat", enrollments: 10 },
                { date: "Sun", enrollments: 6 },
              ];
              setEnrollmentTrends(sampleTrends);
            }

            // Update stats from analytics
            if (analytics.summary) {
              setStats({
                totalEnrollments: analytics.summary.totalEnrollments,
                activeEnrollments: analytics.summary.activeEnrollments,
                completedEnrollments: analytics.summary.completedEnrollments,
                totalRevenue: analytics.summary.totalRevenue,
                averageEnrollmentsPerCourse:
                  analytics.summary.averageEnrollmentsPerCourse,
              });
            }
          }
        } catch (e) {
          console.error("Failed to fetch instructor analytics:", e);
          // Set default trends data
          setEnrollmentTrends([
            { date: "Mon", enrollments: 0 },
            { date: "Tue", enrollments: 0 },
            { date: "Wed", enrollments: 0 },
            { date: "Thu", enrollments: 0 },
            { date: "Fri", enrollments: 0 },
            { date: "Sat", enrollments: 0 },
            { date: "Sun", enrollments: 0 },
          ]);
        }

        // Fetch recent reviews
        try {
          const reviewsRes = await reviewsApi.getInstructorReviews({
            limit: 5,
          });
          if (reviewsRes.success && reviewsRes.data) {
            setRecentReviews(reviewsRes.data);
          }
        } catch (e) {
          console.error("Failed to fetch reviews:", e);
        }
      } catch (err) {
        setError("Failed to load courses");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?._id]);

  // Calculate average rating from courses
  const averageRating =
    courses.length > 0
      ? (
          courses.reduce((acc, c) => acc + (c.rating || 0), 0) / courses.length
        ).toFixed(1)
      : "0.0";

  const staticStats = [
    {
      label: "Total Courses",
      value: courses.length.toString(),
      icon: BookOpen,
      color: "bg-blue-500",
      bgLight: "bg-blue-50",
      textColor: "text-blue-600",
      change: courses.length > 0 ? `+${courses.length}` : "0",
    },
    {
      label: "Total Students",
      value: totalStudents.toLocaleString(),
      icon: Users,
      color: "bg-green-500",
      bgLight: "bg-green-50",
      textColor: "text-green-600",
      change: "+12%",
    },
    {
      label: "Total Revenue",
      value: `${(totalRevenue / 1000).toFixed(0)}k XAF`,
      icon: DollarSign,
      color: "bg-purple-500",
      bgLight: "bg-purple-50",
      textColor: "text-purple-600",
      change: "+18%",
    },
    {
      label: "Avg. Rating",
      value: averageRating,
      icon: Star,
      color: "bg-yellow-500",
      bgLight: "bg-yellow-50",
      textColor: "text-yellow-600",
      change: "+0.2",
    },
  ];

  // Monthly revenue data (from courses)
  const monthlyRevenueData = Array(12)
    .fill(0)
    .map((_, i) => ({
      month: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ][i],
      revenue: 0,
    }));

  // Add revenue from courses if they have enrollment dates
  courses.forEach((course) => {
    if (course.enrolledStudents?.length && course.price) {
      // Simulate distribution across months (in a real app, use actual enrollment dates)
      const revenuePerMonth =
        (course.price * course.enrolledStudents.length) / 12;
      monthlyRevenueData.forEach((month) => {
        month.revenue += revenuePerMonth;
      });
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                Instructor Dashboard
              </h1>
              <p className="text-[10px] text-gray-500 mt-0.5 md:text-xs">
                Manage your courses and track performance
              </p>
            </div>
            <Link
              to="/dashboard/instructor/create-course"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md md:text-xs md:px-4 md:py-2"
            >
              <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" />
              Create Course
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-2 md:grid-cols-4 lg:gap-4">
          {staticStats.map((stat, i) => (
            <div
              key={i}
              className="bg-white rounded-xl p-3 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`w-8 h-8 ${stat.bgLight} rounded-lg flex items-center justify-center`}
                >
                  <stat.icon className={`w-4 h-4 ${stat.textColor}`} />
                </div>
                <span className="text-base font-bold text-gray-900 md:text-lg">
                  {stat.value}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 md:text-xs">
                {stat.label}
              </p>
              <p className="text-[8px] text-green-600 mt-1 md:text-[9px]">
                {stat.change} from last month
              </p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 lg:gap-5">
          {/* Enrollment Trend Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                Enrollment Trends (Last 7 Days)
              </h2>
              <div className="flex items-center gap-1 text-[9px] text-gray-400">
                <Calendar className="w-3 h-3" />
                Weekly
              </div>
            </div>
            <div className="h-48 md:h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={enrollmentTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="enrollments"
                    stroke="#F59E0B"
                    fill="#F59E0B"
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-center">
              <p className="text-[9px] text-gray-500">
                Total enrollments this week:{" "}
                {enrollmentTrends.reduce(
                  (sum, item) => sum + (item.enrollments || 0),
                  0,
                )}
              </p>
            </div>
          </div>

          {/* Top Courses by Students Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <h2 className="text-xs font-bold text-gray-900 mb-3 md:text-sm md:mb-4">
              Top Courses by Students
            </h2>
            <div className="h-48 md:h-56">
              {coursePerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={coursePerformance}
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10 }}
                      stroke="#9ca3af"
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                      width={80}
                    />
                    <Tooltip />
                    <Bar
                      dataKey="students"
                      fill="#10B981"
                      radius={[0, 4, 4, 0]}
                      label={{
                        position: "right",
                        fontSize: 10,
                        fill: "#10B981",
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[11px] text-gray-400">
                    No enrollment data yet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Status Pie Chart & Revenue Overview */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 lg:gap-5">
          {/* Course Status Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <h2 className="text-xs font-bold text-gray-900 mb-3 md:text-sm md:mb-4">
              Course Status
            </h2>
            <div className="h-48 md:h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Published",
                        value: courses.filter((c) => c.isPublished).length || 0,
                      },
                      {
                        name: "Draft",
                        value:
                          courses.filter((c) => !c.isPublished).length || 0,
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: "10px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Revenue Overview Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between md:mb-4">
              <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                Revenue Overview
              </h2>
              <select className="text-[9px] border border-gray-200 rounded-lg px-2 py-1 md:text-xs">
                <option>Last 12 months</option>
                <option>Last 6 months</option>
                <option>Last 30 days</option>
              </select>
            </div>
            <div className="h-48 md:h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10 }}
                    stroke="#9ca3af"
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    stroke="#9ca3af"
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value: number) => [
                      `${value.toLocaleString()} XAF`,
                      "Revenue",
                    ]}
                  />
                  <Bar dataKey="revenue" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col gap-1 mt-2 sm:flex-row sm:items-center sm:justify-between md:mt-3">
              <span className="text-[9px] text-gray-500 md:text-xs">
                Total: {(totalRevenue / 1000).toFixed(0)}k XAF
              </span>
              <span className="text-[9px] text-green-600 flex items-center gap-0.5 md:text-xs">
                <TrendingUp className="w-3 h-3" />
                +18% from last year
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Courses Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                  My Courses
                </h2>
                <Link
                  to="/dashboard/instructor/courses"
                  className="text-[10px] text-orange-600 hover:underline md:text-xs"
                >
                  View All
                </Link>
              </div>

              {courses.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2 text-gray-600 font-medium">
                            Course
                          </th>
                          <th className="text-left p-2 text-gray-600 font-medium">
                            Students
                          </th>
                          <th className="text-left p-2 text-gray-600 font-medium">
                            Rating
                          </th>
                          <th className="text-left p-2 text-gray-600 font-medium">
                            Revenue
                          </th>
                          <th className="text-left p-2 text-gray-600 font-medium">
                            Status
                          </th>
                          <th className="text-left p-2 text-gray-600 font-medium">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {courses.slice(0, 5).map((course) => (
                          <tr
                            key={course._id}
                            className="border-t border-gray-100"
                          >
                            <td className="p-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                                  {(course.thumbnail ||
                                    course.thumbnailUrl) && (
                                    <img
                                      src={
                                        course.thumbnail || course.thumbnailUrl
                                      }
                                      alt={course.title}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                                <span className="font-medium text-gray-900 line-clamp-1 max-w-[150px]">
                                  {course.title}
                                </span>
                              </div>
                            </td>
                            <td className="p-2 text-gray-600">
                              {course.enrolledStudents?.length || 0}
                            </td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                <span className="text-gray-700">
                                  {course.rating?.toFixed(1) || "0.0"}
                                </span>
                              </div>
                            </td>
                            <td className="p-2 text-gray-600">
                              {course.price > 0
                                ? `${course.currency || "XAF"} ${((course.price || 0) * (course.enrolledStudents?.length || 0)).toLocaleString()}`
                                : "Free"}
                            </td>
                            <td className="p-2">
                              <span
                                className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                                  course.isPublished
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}
                              >
                                {course.isPublished ? "Published" : "Draft"}
                              </span>
                            </td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <Link
                                  to={`/dashboard/instructor/courses/${course._id}`}
                                  className="p-1 hover:bg-gray-100 rounded"
                                >
                                  <Eye className="w-3.5 h-3.5 text-gray-500" />
                                </Link>
                                <Link
                                  to={`/dashboard/instructor/courses/${course._id}/edit`}
                                  className="p-1 hover:bg-gray-100 rounded"
                                >
                                  <Edit className="w-3.5 h-3.5 text-gray-500" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="space-y-3 md:hidden">
                    {courses.slice(0, 5).map((course) => (
                      <div
                        key={course._id}
                        className="border border-gray-100 rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                            {(course.thumbnail || course.thumbnailUrl) && (
                              <img
                                src={course.thumbnail || course.thumbnailUrl}
                                alt={course.title}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-[11px] font-semibold text-gray-900 line-clamp-2">
                              {course.title}
                            </h3>
                            <div className="flex items-center gap-3 mt-1.5">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3 text-gray-400" />
                                <span className="text-[9px] text-gray-600">
                                  {course.enrolledStudents?.length || 0}{" "}
                                  students
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                <span className="text-[9px] text-gray-600">
                                  {course.rating?.toFixed(1) || "0.0"}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span
                                className={`px-1.5 py-0.5 rounded-full text-[8px] font-medium ${
                                  course.isPublished
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}
                              >
                                {course.isPublished ? "Published" : "Draft"}
                              </span>
                              <div className="flex items-center gap-2">
                                <Link
                                  to={`/dashboard/instructor/courses/${course._id}`}
                                  className="p-1.5 bg-white rounded-lg border border-gray-200"
                                >
                                  <Eye className="w-3 h-3 text-gray-500" />
                                </Link>
                                <Link
                                  to={`/dashboard/instructor/courses/${course._id}/edit`}
                                  className="p-1.5 bg-white rounded-lg border border-gray-200"
                                >
                                  <Edit className="w-3 h-3 text-gray-500" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-[11px] text-gray-500">
                    No courses found. Create your first course!
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Recent Reviews */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <h2 className="text-xs font-bold text-gray-900 mb-2 md:text-sm md:mb-3">
                Recent Reviews
              </h2>
              <div className="space-y-3">
                {recentReviews.length === 0 ? (
                  <p className="text-[10px] text-gray-500">No reviews yet</p>
                ) : (
                  recentReviews.slice(0, 3).map((review, i) => (
                    <div
                      key={review._id || i}
                      className="border-b border-gray-100 pb-2 last:border-0"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-medium text-gray-900 md:text-xs">
                          {review.user?.name || "Student"}
                        </span>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, j) => (
                            <Star
                              key={j}
                              className={`w-2.5 h-2.5 md:w-3 md:h-3 ${j < review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[9px] text-gray-500 md:text-[10px]">
                        {review.course?.title || "Course"}
                      </p>
                      <p className="text-[9px] text-gray-600 mt-1 line-clamp-2 md:text-[10px]">
                        {review.comment?.length > 80
                          ? `${review.comment.substring(0, 80)}...`
                          : review.comment}
                      </p>
                      <p className="text-[8px] text-gray-400 mt-1 md:text-[9px]">
                        {review.createdAt
                          ? new Date(review.createdAt).toLocaleDateString()
                          : ""}
                      </p>
                    </div>
                  ))
                )}
              </div>
              {recentReviews.length > 0 && (
                <Link
                  to="/dashboard/instructor/reviews"
                  className="flex items-center justify-center gap-0.5 mt-3 text-[9px] text-orange-600 hover:underline md:text-xs"
                >
                  View all reviews <ChevronRight className="w-3 h-3" />
                </Link>
              )}
            </div>

            {/* Pro Tips */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-3 border border-orange-100 md:p-4">
              <h2 className="text-xs font-bold text-gray-900 mb-2 md:text-sm md:mb-3">
                Pro Tips
              </h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-[10px] text-gray-600 md:text-xs">
                  <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Update your course content monthly</span>
                </li>
                <li className="flex items-start gap-2 text-[10px] text-gray-600 md:text-xs">
                  <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Engage with students in Q&A</span>
                </li>
                <li className="flex items-start gap-2 text-[10px] text-gray-600 md:text-xs">
                  <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Promote your courses on social media</span>
                </li>
              </ul>
            </div>

            {/* Quick Stats Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-orange-500" />
                <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                  Quick Stats
                </h2>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[9px] text-gray-500">
                    Completion Rate
                  </span>
                  <span className="text-[10px] font-semibold text-gray-900">
                    {stats?.averageEnrollmentsPerCourse
                      ? (
                          (stats.completedEnrollments /
                            stats.totalEnrollments) *
                          100
                        ).toFixed(0)
                      : 0}
                    %
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[9px] text-gray-500">
                    Active Students
                  </span>
                  <span className="text-[10px] font-semibold text-gray-900">
                    {stats?.activeEnrollments || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[9px] text-gray-500">
                    Avg. Per Course
                  </span>
                  <span className="text-[10px] font-semibold text-gray-900">
                    {stats?.averageEnrollmentsPerCourse?.toFixed(0) || 0}{" "}
                    students
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardInstructor;
